/**
 * Varování před HORNÍMI limity objednávky (částka / hmotnost / počet dílů),
 * KAN-1827. Verdikt i parametry posílá server v ``GET /cart``
 * (``limit_violation`` / ``limit_violation_params``, tytéž kontroly jako
 * pokladna) — klient nic nepočítá, jen podle kódu vybere větu. Používá ho
 * souhrn košíku i konfigurátor, aby zákazník neslyšel stejnou věc dvakrát
 * různými slovy.
 */

import { useTranslation } from "react-i18next";

import { useCart } from "@/api/queries/cart";
import type { Cart } from "@/api/types";
import { useDisplayMoney } from "@/lib/currency";
import { formatCount, formatKgAmount } from "@/lib/weight";

/** Věta pro kód ``limit_violation``; ``null`` = košík je v mezích. */
export function useCartLimitText(
  cart: Pick<Cart, "limit_violation" | "limit_violation_params">,
  locale: string,
): string | null {
  const { t } = useTranslation("cart");
  const { format } = useDisplayMoney(locale);
  const params = cart.limit_violation_params ?? {};
  switch (cart.limit_violation) {
    case "order_above_max_subtotal":
      return t("summary.limit.aboveMaxSubtotal", {
        subtotal: format(Number(params.subtotal)),
        max_subtotal: format(Number(params.max_subtotal)),
      });
    case "order_exceeds_max_weight":
      return t("summary.limit.exceedsMaxWeight", {
        weight_kg: formatKgAmount(params.weight_kg, locale),
        max_weight_kg: formatKgAmount(params.max_weight_kg, locale),
      });
    case "order_items_exceed_max":
      return t("summary.limit.itemsExceedMax", {
        items_count: formatCount(params.items_count, locale),
        max_items: formatCount(params.max_items, locale),
      });
    default:
      return null;
  }
}

/** Varování v konfigurátoru — z košíku, který už má zákazník rozpracovaný. */
export function CartLimitWarning({ locale }: { locale: string }): JSX.Element | null {
  const cart = useCart();
  const text = useCartLimitText(cart.data ?? {}, locale);
  if (text === null) return null;
  return (
    <p className="text-sm text-warn" data-testid="configurator-cart-limit-warning">
      {text}
    </p>
  );
}
